import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Sparkles, CheckCircle2, XCircle, Eye, Loader2, Pencil, Save } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ONBOARDING_SECTIONS, ONBOARDING_EMPTY, SectionForm } from "@/pages/MyOnboarding";

const STEPS = [
  { key: "offer_acceptance", label: "Offer Acceptance" },
  { key: "registration", label: "Employee Registration" },
  { key: "documents_pending", label: "Document Upload" },
  { key: "hr_verification", label: "HR Verification" },
  { key: "it_provisioning", label: "IT Provisioning" },
  { key: "orientation", label: "Orientation" },
  { key: "manager_induction", label: "Manager Induction" },
  { key: "probation", label: "Probation Review" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [refresh, setRefresh] = useState(0);
  const [reviewRec, setReviewRec] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [working, setWorking] = useState(false);

  // HR edit-details state
  const [editMode, setEditMode] = useState(false);
  const [editDetails, setEditDetails] = useState({});
  const [editActive, setEditActive] = useState("personal");
  const [editNote, setEditNote] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const openEdit = (rec) => {
    setEditDetails({ ...ONBOARDING_EMPTY, ...(rec.details || {}) });
    setEditActive("personal");
    setEditNote("");
    setEditMode(true);
  };

  const closeReview = () => {
    setReviewRec(null);
    setRejecting(false);
    setRejectReason("");
    setEditMode(false);
    setEditNote("");
  };

  const updateEditSection = (section, patch) =>
    setEditDetails((d) => ({ ...d, [section]: { ...(d[section] || {}), ...patch } }));

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const { data } = await api.put(`/onboarding/${reviewRec.id}/details`, {
        details: editDetails,
        note: editNote,
      });
      toast.success("Onboarding details updated");
      setReviewRec(data);
      setEditMode(false);
      setEditNote("");
      setRefresh((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally { setSavingEdit(false); }
  };

  const approve = async (rec) => {
    setWorking(true);
    try {
      await api.post(`/onboarding/${rec.id}/approve`, { comment: "" });
      toast.success(`Approved ${rec.candidate_name}'s onboarding`);
      setReviewRec(null);
      setRefresh((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Approve failed");
    } finally { setWorking(false); }
  };

  const reject = async () => {
    if (rejectReason.trim().length < 3) { toast.error("Please provide a reason"); return; }
    setWorking(true);
    try {
      await api.post(`/onboarding/${reviewRec.id}/reject`, { reason: rejectReason });
      toast.success(`Sent back to ${reviewRec.candidate_name} for correction`);
      setReviewRec(null);
      setRejecting(false);
      setRejectReason("");
      setRefresh((v) => v + 1);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reject failed");
    } finally { setWorking(false); }
  };

  return (
    <div data-testid="onboarding-page">
      <PageHeader
        title="Onboarding"
        subtitle="Track every new hire from offer acceptance to probation confirmation. Review and approve employee-submitted onboarding."
        action={
          <Button data-testid="open-wizard" className="h-9 bg-[#F26B3A] hover:bg-[#d85d2f]" onClick={() => navigate("/onboarding/wizard")}>
            <Sparkles className="h-4 w-4 mr-1.5" /> Open joiner wizard
          </Button>
        }
      />

      <Card className="hr-card p-6 mb-6">
        <div className="text-xs uppercase tracking-wider text-gray-500 mb-3">8-step lifecycle</div>
        <div className="flex flex-wrap gap-2">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex items-center gap-2">
              <div className="h-6 w-6 rounded-full bg-[#F26B3A]/10 text-[#0F172A] grid place-items-center text-xs font-semibold">
                {i + 1}
              </div>
              <span className="text-xs text-gray-700">{s.label}</span>
              {i < STEPS.length - 1 && <span className="text-gray-300 mx-1">→</span>}
            </div>
          ))}
        </div>
      </Card>

      <DataTable
        testId="onboarding-table"
        endpoint="/onboarding"
        refreshKey={refresh}
        columns={[
          { key: "candidate_name", label: "Joiner", render: (r) => (
            <div>
              <div className="font-medium">{r.candidate_name}</div>
              <div className="text-[11px] text-gray-500">{r.job_title || r.designation || "—"}</div>
            </div>
          )},
          { key: "joining_date", label: "Joining" },
          { key: "self_onboarding", label: "Type", render: (r) => (
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${
              r.self_onboarding ? "bg-[#1E88E5]/10 text-[#1E88E5]" : "bg-gray-100 text-gray-700"
            }`}>
              {r.self_onboarding ? "Self-submitted" : "HR-managed"}
            </span>
          )},
          { key: "step", label: "Current step", badge: true },
          { key: "progress", label: "Progress", render: (r) => (
            <div className="flex items-center gap-2">
              <div className="w-32 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#1E88E5]" style={{ width: `${r.progress || 0}%` }} />
              </div>
              <span className="text-xs font-mono text-gray-500">{r.progress || 0}%</span>
            </div>
          )},
          { key: "submission_status", label: "Approval", render: (r) => {
            const s = r.submission_status;
            if (!s || s === "draft") return <span className="text-[11px] text-gray-500">Draft</span>;
            const colors = {
              submitted: "bg-amber-100 text-amber-800",
              approved: "bg-emerald-100 text-emerald-800",
              rejected: "bg-rose-100 text-rose-800",
            };
            return <span className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${colors[s] || "bg-gray-100 text-gray-700"}`}>{s}</span>;
          }},
          { key: "status", label: "Status", badge: true },
          { key: "actions", label: "Action", render: (r) => {
            const canEdit = r.self_onboarding && ["draft", "submitted", "rejected", "approved"].includes(r.submission_status || "draft");
            const canReview = r.self_onboarding && r.submission_status === "submitted";
            if (!canEdit && !canReview) return null;
            return (
              <div className="flex items-center gap-1.5">
                {canReview && (
                  <Button
                    size="sm" variant="outline" className="h-7 text-xs"
                    data-testid={`review-onboarding-${r.id}`}
                    onClick={(e) => { e.stopPropagation(); setReviewRec(r); setEditMode(false); }}
                  >
                    <Eye className="h-3.5 w-3.5 mr-1" /> Review
                  </Button>
                )}
                {canEdit && (
                  <Button
                    size="sm" variant="ghost" className="h-7 text-xs text-[#1E88E5] hover:bg-[#1E88E5]/5"
                    data-testid={`edit-onboarding-${r.id}`}
                    onClick={(e) => { e.stopPropagation(); setReviewRec(r); openEdit(r); }}
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                )}
              </div>
            );
          }},
        ]}
      />

      {/* Review / Edit dialog */}
      <Dialog open={!!reviewRec} onOpenChange={(v) => { if (!v) closeReview(); }}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="review-onboarding-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>{editMode ? "Edit onboarding" : "Review onboarding"} — {reviewRec?.candidate_name} ({reviewRec?.employee_id})</span>
              {!editMode && reviewRec?.submission_status && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 capitalize">
                  {reviewRec.submission_status}
                </span>
              )}
              {!editMode && (
                <Button
                  size="sm" variant="outline"
                  data-testid="onboarding-edit-toggle"
                  className="ml-auto h-7 text-xs text-[#1E88E5] border-[#1E88E5]/30"
                  onClick={() => openEdit(reviewRec)}
                >
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit details
                </Button>
              )}
            </DialogTitle>
            {reviewRec?.last_hr_edit_at && !editMode && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5 mt-1.5" data-testid="hr-edit-trail">
                Last edited by HR · <span className="font-medium">{reviewRec.last_hr_edit_by}</span> on {reviewRec.last_hr_edit_at.slice(0, 16).replace("T", " ")}
                {reviewRec.last_hr_edit_note ? <> · <span className="italic">&ldquo;{reviewRec.last_hr_edit_note}&rdquo;</span></> : null}
              </div>
            )}
          </DialogHeader>

          {reviewRec && !editMode && (
            <div className="space-y-5 text-sm overflow-y-auto pr-1">
              <div className="grid grid-cols-3 gap-4 pb-3 border-b border-gray-100">
                <Info label="Department" value={reviewRec.department} />
                <Info label="Designation" value={reviewRec.job_title || reviewRec.designation} />
                <Info label="Submitted on" value={(reviewRec.submitted_at || "").slice(0, 10)} />
              </div>

              {Object.entries(reviewRec.details || {}).map(([sectionKey, sectionVals]) => (
                <div key={sectionKey}>
                  <div className="text-xs uppercase tracking-wider text-gray-400 mb-2">{sectionKey.replace(/_/g, " ")}</div>
                  <div className="grid grid-cols-2 gap-3 bg-gray-50 rounded-md p-3">
                    {Object.entries(sectionVals || {}).map(([k, v]) => (
                      v !== "" && v !== null && v !== undefined && (
                        <Info key={k} label={k.replace(/_/g, " ")} value={String(v)} />
                      )
                    ))}
                  </div>
                </div>
              ))}

              {rejecting && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Reason for rejection</Label>
                  <Textarea
                    rows={3}
                    data-testid="reject-reason"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="e.g. PAN number format is invalid"
                    className="mt-1.5"
                  />
                </div>
              )}
            </div>
          )}

          {reviewRec && editMode && (
            <div className="grid grid-cols-12 gap-4 overflow-y-auto" data-testid="onboarding-edit-form">
              {/* Section sidebar */}
              <div className="col-span-3 border-r border-gray-100 pr-2 py-1">
                <div className="text-[10px] uppercase tracking-wider text-gray-400 px-2 py-1">Sections</div>
                {ONBOARDING_SECTIONS.filter((s) => s.key !== "review").map((s) => (
                  <button
                    key={s.key}
                    data-testid={`hr-edit-section-${s.key}`}
                    type="button"
                    onClick={() => setEditActive(s.key)}
                    className={`w-full text-left px-2 py-1.5 rounded-md flex items-center gap-2 text-sm ${
                      editActive === s.key ? "bg-[#1E88E5]/10 text-[#0F172A] font-medium" : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <s.icon className="h-3.5 w-3.5" />
                    <span className="flex-1">{s.label}</span>
                  </button>
                ))}
              </div>
              {/* Form */}
              <div className="col-span-9 py-1 px-1">
                <SectionForm
                  section={editActive}
                  data={editDetails[editActive] || {}}
                  onChange={(patch) => updateEditSection(editActive, patch)}
                  readOnly={false}
                  allData={editDetails}
                  updateRoot={(section, patch) => updateEditSection(section, patch)}
                  setActive={setEditActive}
                />
                <div className="mt-5 pt-4 border-t border-gray-100">
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Edit note (audit trail)</Label>
                  <Input
                    data-testid="hr-edit-note"
                    placeholder="e.g. Fixed Aadhaar typo per employee's request"
                    value={editNote}
                    onChange={(e) => setEditNote(e.target.value)}
                    className="mt-1.5"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">This note plus your email and timestamp will be saved in the audit log.</p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex-wrap gap-2">
            {!editMode && !rejecting && (
              <>
                <Button
                  variant="outline"
                  data-testid="onboarding-reject-open"
                  onClick={() => setRejecting(true)}
                  disabled={reviewRec?.submission_status !== "submitted"}
                  title={reviewRec?.submission_status !== "submitted" ? "Only submitted records can be sent back" : ""}
                >
                  <XCircle className="h-4 w-4 mr-1.5 text-rose-600" /> Send back for correction
                </Button>
                <Button
                  data-testid="onboarding-approve"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => approve(reviewRec)}
                  disabled={working || reviewRec?.submission_status !== "submitted"}
                >
                  {working ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve</>}
                </Button>
              </>
            )}
            {!editMode && rejecting && (
              <>
                <Button variant="outline" onClick={() => { setRejecting(false); setRejectReason(""); }} data-testid="onboarding-reject-cancel">Cancel</Button>
                <Button
                  data-testid="onboarding-reject-confirm"
                  className="bg-rose-600 hover:bg-rose-700"
                  onClick={reject}
                  disabled={working}
                >
                  {working ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send back"}
                </Button>
              </>
            )}
            {editMode && (
              <>
                <Button variant="outline" onClick={() => { setEditMode(false); setEditNote(""); }} data-testid="hr-edit-cancel">
                  Cancel
                </Button>
                <Button
                  data-testid="hr-edit-save"
                  className="bg-[#1E88E5] hover:bg-[#1976D2]"
                  onClick={saveEdit}
                  disabled={savingEdit}
                >
                  {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> Save edits</>}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-gray-400">{label}</div>
      <div className="text-gray-800 text-sm break-words">{value || "—"}</div>
    </div>
  );
}
